/* import __COLOCATED_TEMPLATE__ from './channel-group-selector.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';

interface Args {
  targetChannels: string[];
  onChange: (channels: string[]) => void;
  disabled?: boolean;
  category?: GuidelineCategory;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const CHANNEL_GROUPS = ['chat', 'email', 'voice'] as const;
type ChannelGroup = (typeof CHANNEL_GROUPS)[number];

const VOICE_UNSUPPORTED_CATEGORIES: GuidelineCategory[] = [
  'content',
  'spam_detection',
  'other',
  'copilot',
  'routing',
  'voice_routing',
];

export default class ChannelGroupSelector extends Component<Signature> {
  @service intl!: IntlService;

  private labelForGroup(group: ChannelGroup): string {
    if (group === 'chat') {
      return this.intl.t('ai-agent.guidance.channel-groups.chat');
    } else if (group === 'email') {
      return this.intl.t('ai-agent.guidance.channel-groups.email');
    } else {
      return this.intl.t('ai-agent.guidance.channel-groups.voice');
    }
  }

  private iconForGroup(group: ChannelGroup): InterfaceIconName {
    if (group === 'chat') {
      return 'chat-bubble';
    } else if (group === 'email') {
      return 'email-filled';
    } else {
      return 'phone-mic-on';
    }
  }

  private get isVoiceDisabled(): boolean {
    return this.args.category ? VOICE_UNSUPPORTED_CATEGORIES.includes(this.args.category) : false;
  }

  get items() {
    let items = CHANNEL_GROUPS.map((group) => ({
      text: this.labelForGroup(group),
      icon: this.iconForGroup(group),
      value: group,
      isSelected: this.args.targetChannels?.includes(group) ?? false,
      ...(group === 'voice' && this.isVoiceDisabled
        ? {
            isDisabled: true,
            tooltipText: this.intl.t('ai-agent.guidance.channel-groups.coming-soon-voice'),
          }
        : {}),
    }));
    return [{ items }];
  }

  get displayText(): string {
    let selected = this.args.targetChannels;
    if (!selected || selected.length === 0 || selected.length === CHANNEL_GROUPS.length) {
      return this.intl.t('ai-agent.guidance.channel-groups.all-channels');
    }
    return selected.map((group) => this.labelForGroup(group as ChannelGroup)).join(', ');
  }

  @action
  handleSelectionChange(selectedValues: string[]) {
    this.args.onChange(selectedValues);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::ChannelGroupSelector': typeof ChannelGroupSelector;
    'ai-agent/guidance/channel-group-selector': typeof ChannelGroupSelector;
  }
}
